import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import {
    DEFAULT_SORT,
    adsDrilldownTableColumns,
} from "app/pages/keyword-research/website-analysis/pages/landing-pages/OrganicLandingPagesColumnsConfigV2";
import { translate } from "app/filters/i18nFilter";
import type { FC } from "react";
import { getShareValue, getVolumeValue } from "../../../../website-analysis/traffic-sources/search/common/utils";
import { useLandingPagesEndpoints } from "./landingPagesEndpoints";
import { sourceTypeMap } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { allTrackers } from "app/services/track/track";
import { adTypeColumnIconConfig } from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";

interface IDataRecord {
    AdType: string;
    Title: string;
    Image: string;
    Price: string;
    Brand: string;
    Clicks: number;
    ClicksShare: number;
}

interface IOrganicLandingPageEnrichedAdsTableProps {
    landingPage: string;
    sort?: string;
    asc?: boolean;
}

export const OrganicLandingPageEnrichedAdsTable: FC<IOrganicLandingPageEnrichedAdsTableProps> = ({
    landingPage,
    sort = DEFAULT_SORT,
    asc = false,
}) => {
    const { params } = useCurrentStateAndParams();
    const { to, from, isWindow } = proNavigator.getApiParams();
    const endpoints = useLandingPagesEndpoints();

    const apiParams = {
        country: params.country,
        webSource: params.webSource,
        includeSubDomains: params.isWWW === "*",
        to,
        from,
        isWindow,
        url: landingPage,
        sort,
        asc,
        sourceType: sourceTypeMap.Paid,
    };

    const transformData = (data: { Data: IDataRecord[] }) => {
        return {
            ...data,
            Data: (data?.Data ?? [])
                .map((record: IDataRecord) => {
                    if (!record.AdType) {
                        return;
                    }
                    const { Clicks, ClicksShare, AdType, ...rest } = record;
                    const { iconName, tooltip } = adTypeColumnIconConfig[AdType];
                    return {
                        ClicksShare: getShareValue(ClicksShare),
                        Clicks: getVolumeValue(Clicks),
                        AdDetails: { ...rest, AdType, DestUrl: landingPage },
                        iconDetails: { iconName, tooltip: translate(tooltip) },
                    };
                })
                .filter(Boolean),
        };
    };

    const changePageCallback = (page: number) => {
        allTrackers.dispatchTrackEvent("Pagination", "switch", `page ${page}`);
    };

    return (
        <div className="swReactTable-wrapper-adsCount">
            <SWReactTableWrapper
                transformData={transformData}
                initialFilters={apiParams}
                serverApi={endpoints.pageAds}
                tableColumns={adsDrilldownTableColumns}
                tableOptions={{
                    noDataI18n: "organic.landing.pages.inner.table.nodata",
                    longLoader: {
                        title: translate("organic.landing.pages.inner.table.loader.title"),
                        subtitle: translate("organic.landing.pages.inner.table.loader.subtitle"),
                    },
                    metric: "OrganicPagesKeywordsV2_Table",
                    customTableClass: "organic-landing-pages-ads-table",
                }}
                recordsField="Data"
                totalRecordsField="TotalCount"
                changePageCallback={changePageCallback}
                fetchServerPages={Number.MAX_SAFE_INTEGER}
                rowsPerPage={3}
            />
        </div>
    );
};
